// apps/share-web/src/offline/freshness.ts
//
// Состояние строки под «Team library». Чистая функция: вход — четыре факта,
// выход — одно из пяти состояний. Ни DOM, ни i18n, ни часов: `nowMs` приходит
// параметром, иначе тест на границу «только что» пришлось бы писать с
// подменой системного времени.
//
// Почему «обновляю» побеждает всё остальное: строка отвечает на вопрос «что
// сейчас происходит», и пока запрос в воздухе, происходит именно это. Возраст
// кэша вернётся на экран через долю секунды, когда запрос разрешится.
//
// `unknown` — не ошибка, а «сказать нечего»: успеха не было ни разу. Вьюха
// такую строку прячет; о полном отсутствии данных говорит пустое состояние
// библиотеки, а не подпись под заголовком.
//
// ⚠️ КОНТРАКТ ПОЛЯ `online`, и он не тот, о котором думаешь — уточнено
// дефектом 4Б живой приёмки волны G (владелец продукта: приложение обязано
// заметить потерю сети САМО, без перезагрузки).
//
// `online` означает «ПОСЛЕДНИЙ ЗАПРОС К СЕРВЕРУ УДАЛСЯ», И браузер не сказал
// обратное. Прежняя редакция запрещала `navigator.onLine` ЦЕЛИКОМ — это было
// шире факта. Точное правило: `navigator.onLine` врёт ТОЛЬКО в сторону
// `true`. На зальном Wi-Fi с мёртвым аплинком и за captive-порталом он
// возвращает `true` — интерфейс поднят, адрес получен, наружу не идёт
// ничего, — и ИМЕННО этот положительный ответ доверия не заслуживает: строка
// сказала бы «обновлено вчера» вместо «офлайн · обновлено вчера», то есть
// умолчала бы ровно там, где человеку нужно понять, что свежих слов у него
// нет. Отрицательный ответ (`false`) в эту ловушку не попадает — интерфейс,
// который сам браузер считает поднятым, не может быть ложным «нет сети»;
// живой пример из прежней редакции («туннель в метро с VPN») описывал
// именно эту, достоверную сторону, и был приведён здесь как контрпример по
// ошибке.
//
// Эта функция ОСТАЁТСЯ ЧИСТОЙ: `online` — один готовый булев вход, часов и
// browser API здесь как не было, так и нет. Комбинация «исход запроса И
// браузер не сказал `false`» — дело ВЫЗЫВАЮЩЕГО (main.ts, `freshnessNow` в
// ветке `/library`), а не этой функции: там оба факта сходятся, и оттуда же
// событие `offline` браузера способно перерисовать строку без нового
// запроса к серверу.
//
// Проводка (задача 11, дефект 4Б) обязана держать `lastFetchOk` сама:
// выставлять `true` после успешного ответа и `false` после отказа. Вето
// браузера — ОТДЕЛЬНЫЙ множитель поверх этого значения, не замена ему:
// событие `offline` не может обелить провалившийся запрос, а истинный
// `navigator.onLine` не может опровергнуть его же успех, случившийся секунду
// назад.

export type FreshnessState =
  | { kind: 'syncing' }
  | { kind: 'justNow' }
  | { kind: 'at'; iso: string }
  | { kind: 'offlineAt'; iso: string }
  | { kind: 'unknown' };

export const JUST_NOW_MS = 60_000;

export interface FreshnessInput {
  online: boolean;
  syncing: boolean;
  lastSuccessAt: string | null;
  nowMs: number;
}

export function freshness(input: FreshnessInput): FreshnessState {
  if (input.syncing) return { kind: 'syncing' };

  const iso = input.lastSuccessAt;
  // ДЕКЛАРАЦИЯ НАМЕРЕНИЯ, а не защита: `null` — объявленное типом состояние
  // «успеха не было ни разу», и оно названо здесь отдельной строкой. Тестом
  // эта ветка не закрыта и закрыта быть не может — её удаление оставляет
  // сьют зелёным, потому что `Date.parse(null as any)` тоже даёт NaN и
  // ответ сходится случайно, через приведение к строке 'null'. Опираться на
  // это совпадение в единственном месте, где `null` штатен, не хочется;
  // строка стоит ценой одного сравнения. Замком её НЕ называем.
  if (!iso) return { kind: 'unknown' };
  // А вот это — защита от нечитаемой метки: в кэше лежит то, что когда-то
  // записала прежняя версия приложения.
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return { kind: 'unknown' };

  if (!input.online) return { kind: 'offlineAt', iso };
  if (input.nowMs - t < JUST_NOW_MS) return { kind: 'justNow' };
  return { kind: 'at', iso };
}
